
import React, { useState, useEffect } from 'react';
import { UserData, EmploymentType } from '../types';

interface QuizProps {
  onSubmit: (data: UserData) => void;
  initialData?: UserData | null;
}

const questions = [
  {
    key: 'contact_info', // Composite step
    question: "Let's get started. Who are we helping today?",
    type: 'contact-details',
    required: true,
  },
  {
    key: 'employmentType',
    question: 'What is your primary employment sector?',
    options: [
      { value: 'public', label: 'Public Service (Government)' },
      { value: 'non-profit', label: 'Non-Profit Organization' },
      { value: 'private', label: 'Private (For-Profit)' },
      { value: 'not-employed', label: 'Not Currently Employed' },
    ],
    type: 'radio',
  },
  {
    key: 'yearsInPublicService',
    question: 'How many years have you worked in public service or for a non-profit?',
    type: 'slider',
    condition: (answers: Partial<UserData>) => answers.employmentType === 'public' || answers.employmentType === 'non-profit',
  },
  {
    key: 'loanEstimatedBalance',
    question: 'Roughly how much do you currently owe in student loans?',
    options: [
      { value: 5000, label: 'Under $10,000' },
      { value: 25000, label: '$10,000 - $50,000' },
      { value: 75000, label: '$50,000 - $100,000' },
      { value: 150000, label: '$100,000 - $200,000' },
      { value: 250000, label: 'More than $200,000' },
    ],
    type: 'radio',
  },
  {
    key: 'loanServicer',
    question: 'Who currently services your federal loans?',
    options: [
      { value: 'mohela', label: 'MOHELA' },
      { value: 'aidvantage', label: 'Aidvantage' },
      { value: 'nelnet', label: 'Nelnet' },
      { value: 'edfinancial', label: 'EdFinancial' },
      { value: 'other', label: 'Other / I don\'t know' },
    ],
    type: 'radio',
  },
  {
    key: 'onIDR',
    question: 'Are you currently on an Income-Driven Repayment (IDR) plan like SAVE, PAYE, or REPAYE?',
     options: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No / I\'m not sure' },
    ],
    type: 'radio',
  },
  {
    key: 'hasDisability',
    question: 'Do you have a total and permanent disability as recognized by a physician or the SSA/VA?',
     options: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ],
    type: 'radio',
  },
  {
    key: 'referralSource',
    question: 'How did you hear about us?',
    options: [
      { value: 'google', label: 'Google Search' },
      { value: 'social', label: 'Social Media' },
      { value: 'friend', label: 'Friend or Colleague' },
      { value: 'news', label: 'News or Article' },
      { value: 'other', label: 'Other' },
    ],
    type: 'radio',
  }
];

const Quiz: React.FC<QuizProps> = ({ onSubmit, initialData }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Partial<UserData>>(initialData || {
    name: '',
    email: '',
    phoneNumber: '',
    employmentType: 'private',
    yearsInPublicService: 0,
    hasDisability: false,
    onIDR: false,
    referralSource: 'google', // Default
    loanEstimatedBalance: 25000, // Default mid-range
    loanServicer: 'other'
  });
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  // Ensure answers update if initialData loads later (e.g. from local storage fetch)
  useEffect(() => {
    if (initialData) {
        setAnswers(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const activeQuestions = questions.filter(q => !q.condition || q.condition(answers));
  const totalSteps = activeQuestions.length;
  const currentQuestion = activeQuestions[currentStep];

  const handleAnswer = (key: keyof UserData, value: any) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
        setErrors(prev => ({...prev, [key]: undefined}));
    }
  };

  const validateStep = () => {
    if (currentQuestion.type === 'contact-details') {
        const newErrors: Record<string, string> = {};
        
        // Name Validation
        if (!answers.name || !answers.name.trim()) {
            newErrors.name = 'Name is required.';
        }
        
        // Email Validation
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!answers.email || !emailRegex.test(answers.email.trim())) {
            newErrors.email = 'Please enter a valid email address.';
        }
        
        // Phone Validation (Optional but strict if entered)
        if (answers.phoneNumber) {
            const phoneDigits = answers.phoneNumber.replace(/\D/g, '');
            if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
                newErrors.phoneNumber = 'Please enter a valid 10-digit phone number.';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const currentKey = currentQuestion.key as keyof UserData;
    const value = answers[currentKey];
    let error = '';

    if (currentQuestion.required && (!value || String(value).trim() === '')) {
        error = 'This field is required.';
    } else if (currentKey === 'email' && value) {
        // Fallback for standalone email questions if added later
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(String(value).trim())) {
             error = 'Please enter a valid email address.';
        }
    }
    
    setErrors(prev => ({...prev, [currentKey]: error }));
    return !error;
  };

  const nextStep = () => {
    if (!validateStep()) return;

    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onSubmit(answers as UserData);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderInput = () => {
    const error = errors[currentQuestion.key as keyof UserData]; // Fallback for simple fields
    const inputClasses = "w-full p-4 text-lg border-2 rounded-lg focus:ring-2 focus:outline-none transition";
    const normalClasses = "border-gray-300 focus:ring-green-500 focus:border-green-500";
    const errorClasses = "border-red-500 focus:ring-red-500 focus:border-red-500";

    const renderInputField = (key: keyof UserData, type: 'text' | 'email' | 'tel', placeholder: string, required = false) => (
        <div>
            <input
                type={type}
                value={answers[key] as string || ''}
                onChange={(e) => handleAnswer(key, e.target.value)}
                className={`${inputClasses} ${errors[key] ? errorClasses : normalClasses}`}
                placeholder={placeholder}
            />
            {errors[key] && <p className="text-red-600 text-sm mt-2">{errors[key]}</p>}
        </div>
    );
    
    switch (currentQuestion.type) {
      case 'contact-details':
        return (
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                    {renderInputField('name', 'text', 'Jane Doe', true)}
                </div>
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                    {renderInputField('email', 'email', 'jane@example.com', true)}
                </div>
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number <span className="text-gray-400 font-normal">(Optional)</span></label>
                    {renderInputField('phoneNumber', 'tel', '(555) 555-5555')}
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                    We use this to save your results and send you updates.
                </p>
            </div>
        );
      case 'text':
        return renderInputField(currentQuestion.key as keyof UserData, 'text', `Enter your ${currentQuestion.key}...`);
      case 'email':
        return renderInputField(currentQuestion.key as keyof UserData, 'email', `Enter your email...`);
      case 'radio':
        return (
          <div className="space-y-3">
            {currentQuestion.options?.map(option => (
              <label
                key={`${option.value}`}
                className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                  answers[currentQuestion.key as keyof UserData] === option.value
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-blue-500'
                }`}
              >
                <input
                  type="radio"
                  name={currentQuestion.key}
                  value={`${option.value}`}
                  checked={answers[currentQuestion.key as keyof UserData] === option.value}
                  onChange={() => handleAnswer(currentQuestion.key as keyof UserData, option.value)}
                  className="hidden"
                />
                <span className="text-lg font-medium text-gray-800">{option.label}</span>
              </label>
            ))}
          </div>
        );
      case 'slider':
        const years = answers.yearsInPublicService || 0;
        return (
          <div className="flex flex-col items-center space-y-4">
            <span className="text-5xl font-bold text-green-500">{years} {years === 1 ? 'Year' : 'Years'}</span>
            <input
              type="range"
              min="0"
              max="30"
              step="1"
              value={years}
              onChange={(e) => handleAnswer('yearsInPublicService', parseInt(e.target.value, 10))}
              className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer range-lg accent-green-500"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-xl max-w-2xl mx-auto">
      <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">Check Your Forgiveness Eligibility</h1>
          <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-600">
              Answer a few simple questions—we’ll do the heavy lifting for you.
          </p>
      </div>
      
      <div className="mb-8">
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
          <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}></div>
        </div>
        <p className="text-sm text-gray-500">Step {currentStep + 1} of {totalSteps}</p>
      </div>
      
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 min-h-[80px] flex items-center">{currentQuestion.question}</h2>
      
      <div className="mb-8">{renderInput()}</div>

      <div className="flex justify-between items-center">
        <button
          onClick={prevStep}
          disabled={currentStep === 0}
          className="text-gray-500 hover:text-gray-800 font-semibold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Back
        </button>
        <button
          onClick={nextStep}
          className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-full shadow-lg shadow-green-500/20 transition-all duration-300 transform hover:scale-105"
        >
          {currentStep === totalSteps - 1 ? 'Finish & Analyze' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Quiz;
